export type PostAuthRedirectSource = 'router' | 'app' | '401'

export interface PostAuthRedirect {
  fullPath: string
  path: string
  selectedTab?: string
  source: PostAuthRedirectSource
  ts: number
}

const STORAGE_KEY = 'msq.postAuthRedirect'
const MAX_AGE_MS = 30 * 60 * 1000
const BLOCKED_PATHS = new Set<string>(['/sign-in', '/register', '/forgot-password'])
const BLOCKED_PREFIXES = ['/reset-password']
const VALID_SOURCES = new Set<PostAuthRedirectSource>(['router', 'app', '401'])

function isBlockedPath(path: string): boolean {
  if (BLOCKED_PATHS.has(path)) return true
  return BLOCKED_PREFIXES.some((prefix) => path.startsWith(prefix))
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    if (!('sessionStorage' in window)) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function isValidRedirect(value: unknown): value is PostAuthRedirect {
  if (value == null || typeof value !== 'object') return false
  const candidate = value as PostAuthRedirect
  if (typeof candidate.fullPath !== 'string') return false
  if (typeof candidate.path !== 'string') return false
  if (typeof candidate.ts !== 'number') return false
  if (!VALID_SOURCES.has(candidate.source)) return false
  if (candidate.selectedTab != null && typeof candidate.selectedTab !== 'string') return false
  return true
}

export function setPostAuthRedirect(payload: Omit<PostAuthRedirect, 'ts'>): void {
  if (isBlockedPath(payload.path)) return
  const store = getStorage()
  if (!store) return
  const value: PostAuthRedirect = { ...payload, ts: Date.now() }
  store.setItem(STORAGE_KEY, JSON.stringify(value))
}

export function getPostAuthRedirect(): PostAuthRedirect | null {
  const store = getStorage()
  if (!store) return null
  const raw = store.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!isValidRedirect(parsed)) {
      store.removeItem(STORAGE_KEY)
      return null
    }
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      store.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    store.removeItem(STORAGE_KEY)
    return null
  }
}

export function clearPostAuthRedirect(): void {
  const store = getStorage()
  if (!store) return
  store.removeItem(STORAGE_KEY)
}

export function consumePostAuthRedirect(): PostAuthRedirect | null {
  const value = getPostAuthRedirect()
  if (value) clearPostAuthRedirect()
  return value
}
